import { useState } from 'react'
import type { Availability, WorkRow } from '@workflow/contracts'
import { PLAYER_WORK_STATUSES } from '@workflow/contracts'
import { ActionMenu, EmptyNote, Icons, ProgressRing, StatusLabel, Truncate } from '@workflow/ui'
import { formatDay } from '@/lib/format'
import { formatHours } from '@/features/jobs/jobsStatus'
import { FinishedPrompt, TaskWindow, type TaskIntent } from './TaskWindow'
import { HourAdjustments, assignedShortText, assignedText, isLive, rowState } from './workParts'

/**
 * The player's active tasks, newest first — one quiet line each: the work's
 * name, the minute it was assigned, and the hours as a ring with the figures
 * beside it. Everything else waits one press away: the row opens the task
 * window (details, the hours counter, a note, the finish), and the three
 * dots reach the same verbs directly. Nothing hides behind a hover, so a
 * phone reads exactly what a desk does.
 */
export function LiveWork({ rows, availability }: { rows: WorkRow[]; availability: Availability }) {
  const live = rows.filter(isLive).sort((a, b) => b.assignedAt.localeCompare(a.assignedAt))
  const [open, setOpen] = useState<{ id: string; intent: TaskIntent } | null>(null)
  // A task finished by its last hour leaves the active list at once, so the
  // offer of a note — and the note window after it — hold it themselves.
  const [finished, setFinished] = useState<{ row: WorkRow; writing: boolean } | null>(null)
  const current = open ? live.find((row) => row.assignmentId === open.id) ?? null : null
  return (
    <>
      {live.length === 0 ? (
        <EmptyNote title="No active tasks." detail="New assignments will appear here." />
      ) : (
        <ul className="-mx-3 flex flex-col">
          {live.map((row) => (
            <TaskRow key={row.assignmentId} row={row} onOpen={(intent) => setOpen({ id: row.assignmentId, intent })} />
          ))}
        </ul>
      )}
      <TaskWindow
        row={finished?.writing ? finished.row : current}
        intent={finished?.writing ? 'note' : open?.intent ?? 'view'}
        availability={availability}
        onClose={() => {
          setOpen(null)
          setFinished(null)
        }}
        onFinished={(row) => {
          setOpen(null)
          setFinished({ row, writing: false })
        }}
      />
      <FinishedPrompt
        row={finished && !finished.writing ? finished.row : null}
        onWrite={() => setFinished((value) => (value ? { ...value, writing: true } : null))}
        onClose={() => setFinished(null)}
      />
    </>
  )
}

/** The hours as a capsule: done over assigned, or done alone when open-ended. */
function HoursFigures({ done, total }: { done: number; total: number | null }) {
  return (
    <span className="whitespace-nowrap rounded-full bg-tint px-2 py-0.5 font-mono text-micro tabular-nums text-foreground">
      {formatHours(done)}
      {total !== null ? <span className="text-muted-foreground"> / {formatHours(total)}</span> : null}
    </span>
  )
}

function TaskRow({ row, onOpen }: { row: WorkRow; onOpen: (intent: TaskIntent) => void }) {
  const working = PLAYER_WORK_STATUSES.includes(row.jobStatus)
  const done = row.actualHours ?? 0
  const total = row.estimatedHours
  // Only a state that asks something of the player earns a word on the line:
  // a pause, or work not yet begun. In progress is what the ring already says.
  const flag = !working ? 'Paused' : row.completion === 'assigned' ? 'Not Started' : null

  return (
    <li className="flex items-center gap-1 rounded-2xl pr-1 transition-colors duration-fast hover:bg-tint">
      <button
        type="button"
        onClick={() => onOpen('view')}
        aria-label={`Open ${row.jobTitle}`}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl py-3 pl-3 sm:gap-3.5 text-left outline-none focus-visible:ring-1 focus-visible:ring-foreground"
      >
        <ProgressRing
          value={total ? done / total : 0}
          size={34}
          label={`${formatHours(done)} of ${total !== null ? formatHours(total) : 'open'} hours`}
          className="shrink-0"
        />
        <span className="min-w-0 flex-1">
          <Truncate text={row.jobTitle} className="text-ui font-medium text-foreground" />
          <span className="mt-0.5 block truncate text-caption text-muted-foreground">
            {/* A phone shares this line with the tag and the hours, so it drops this year's year. */}
            <span className="hidden sm:inline">{assignedText(row.assignedAt)}</span>
            <span className="sm:hidden">{assignedShortText(row.assignedAt)}</span>
          </span>
        </span>
        {/* The state's tag stands beside the hours, on the same line: the word first, then the count. */}
        <span className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {flag ? <span className="whitespace-nowrap rounded-full bg-tint px-1.5 py-0.5 text-micro text-foreground sm:px-2">{flag}</span> : null}
          <HoursFigures done={done} total={total} />
        </span>
      </button>
      <ActionMenu
        label={`Actions for ${row.jobTitle}`}
        sections={[{
          items: [
            { key: 'view', label: 'View Details', icon: <Icons.review size="sm" />, onSelect: () => onOpen('view') },
            ...(working ? [
              { key: 'hours', label: 'Update Hours', icon: <Icons.time size="sm" />, onSelect: () => onOpen('hours') },
              { key: 'complete', label: 'Mark As Complete', icon: <Icons.complete size="sm" />, onSelect: () => onOpen('complete') },
            ] : []),
            { key: 'note', label: 'Write a Note', icon: <Icons.note size="sm" />, onSelect: () => onOpen('note') },
          ],
        }]}
      />
    </li>
  )
}

const MONTH = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' })

const monthOf = (row: WorkRow): string => {
  const at = row.completedAt ?? row.assignedAt
  const date = new Date(at)
  return Number.isNaN(date.getTime()) ? 'Undated' : MONTH.format(date)
}

/**
 * The Work page: every assignment. Active tasks first, as the same rows Home
 * shows — open one, log hours, finish it, write a note. Then finished and
 * closed work by month, newest first: a press opens its details to read,
 * with nothing left to change.
 */
export function WorkLedger({ rows, availability }: { rows: WorkRow[]; availability: Availability }) {
  const [viewing, setViewing] = useState<WorkRow | null>(null)
  if (rows.length === 0) return <EmptyNote title="No work yet." detail="Assigned tasks will appear here." />
  const live = rows.filter(isLive).sort((a, b) => b.assignedAt.localeCompare(a.assignedAt))
  const past = rows
    .filter((row) => !isLive(row))
    .sort((a, b) => (b.completedAt ?? b.assignedAt).localeCompare(a.completedAt ?? a.assignedAt))
  const months = new Map<string, WorkRow[]>()
  for (const row of past) {
    const month = monthOf(row)
    months.set(month, [...(months.get(month) ?? []), row])
  }

  return (
    <div className="flex flex-col gap-8">
      {live.length > 0 ? (
        <section>
          <h2 className="mb-1 text-micro text-muted-foreground uppercase">Active</h2>
          <LiveWork rows={live} availability={availability} />
        </section>
      ) : null}
      {[...months.entries()].map(([month, monthRows]) => (
        <LedgerGroup key={month} title={month} rows={monthRows} onOpen={setViewing} />
      ))}
      <TaskWindow row={viewing} intent="view" availability={availability} onClose={() => setViewing(null)} readOnly />
    </div>
  )
}

function LedgerGroup({ title, rows, onOpen }: { title: string; rows: WorkRow[]; onOpen: (row: WorkRow) => void }) {
  return (
    <section>
      <h2 className="mb-1 text-micro text-muted-foreground uppercase">{title}</h2>
      <ul>
        {rows.map((row) => (
          <LedgerRow key={row.assignmentId} row={row} onOpen={() => onOpen(row)} />
        ))}
      </ul>
    </section>
  )
}

function LedgerRow({ row, onOpen }: { row: WorkRow; onOpen: () => void }) {
  const state = rowState(row)
  const day = row.completedAt ?? row.assignedAt
  return (
    <li className="border-b border-hairline py-1 last:border-0">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`View ${row.jobTitle}`}
        className="-mx-3 grid w-[calc(100%+1.5rem)] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 rounded-2xl px-3 py-2.5 text-left outline-none transition-colors duration-fast hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground sm:grid-cols-[minmax(0,1fr)_8.5rem_5.5rem_6.25rem]"
      >
        <span className="min-w-0">
          <Truncate text={row.jobTitle} className="text-ui font-medium text-foreground" />
          <span className="mt-0.5 block truncate text-caption text-muted-foreground">
            {[row.clientName ?? 'Internal', row.quality !== null ? `Rated ${row.quality}/5` : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
        <span className="hidden text-caption tabular-nums text-muted-foreground sm:block">{formatDay(day)}</span>
        <span className="hidden sm:block"><HoursFigures done={row.actualHours ?? 0} total={row.estimatedHours} /></span>
        <StatusLabel className="justify-self-end whitespace-nowrap sm:justify-self-start" tone={state.tone} hue={state.hue} label={state.label} />
      </button>
      <HourAdjustments assignmentId={row.assignmentId} className="mb-2" />
    </li>
  )
}
